import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { site, heroSlides, heroImages, heroVideo, gallery, featuredCatches } from "../data/mock";
import { useTranslation } from "react-i18next";
import { useTrips, useSpeciesList, useTestimonials } from "../i18n/content";
import { Reveal, SectionTitle, Eyebrow, TripCard, SpeciesCard, StarRow, CTASection } from "../components/common";
import WeatherWidget from "../components/WeatherWidget";
import TideTracker from "../components/TideTracker";
import Partners from "../components/Partners";
import Seo from "../components/Seo";
import { Button } from "../components/ui/button";
import { useDeposit } from "../context/DepositContext";
import { Anchor, Compass, Fish, Waves, Ship, ArrowRight, Play, ShieldCheck, Gauge, Users, MessageCircle, CloudSun, Facebook, ChevronLeft, ChevronRight } from "lucide-react";

const HeroMedia = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (heroVideo) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % heroSlides.length), 4500);
    return () => clearInterval(t);
  }, []);
  if (heroVideo) {
    return (
      <video autoPlay muted loop playsInline className="absolute inset-0 h-full w-full object-cover">
        <source src={heroVideo} type="video/mp4" />
      </video>
    );
  }
  return (
    <>
      {heroSlides.map((s, i) => (
        <div key={i} className="absolute inset-0 transition-opacity duration-1000" style={{ opacity: i === idx ? 1 : 0 }}>
          <img src={s.img} alt={s.label} style={s.pos ? { objectPosition: s.pos } : undefined} className={`h-full w-full object-cover ${s.cls || "object-center"} ${i === idx ? "animate-kenburns" : ""}`} />
        </div>
      ))}
    </>
  );
};

const CatchOfTheWeek = () => {
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = featuredCatches.length;
  useEffect(() => {
    if (paused) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 5000);
    return () => clearInterval(t);
  }, [paused, n]);
  const c = featuredCatches[idx];
  const go = (d) => setIdx((i) => (i + d + n) % n);
  return (
    <section className="py-16 bg-[#071726] border-y border-white/5" data-testid="catch-of-the-week">
      <div className="container-x">
        <SectionTitle eyebrow={t("home.catchEyebrow")} title={t("home.catchTitle")} sub={t("home.catchSub")} />
        <div
          className="relative overflow-hidden rounded-3xl border border-white/10 bg-[#0e2740]"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <div className="grid md:grid-cols-2">
            <div className="relative h-72 md:h-[26rem] overflow-hidden">
              {featuredCatches.map((f, i) => (
                <img
                  key={i}
                  src={f.img}
                  alt={f.species}
                  className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
                  style={{ opacity: i === idx ? 1 : 0 }}
                  data-testid={`catch-image-${i}`}
                />
              ))}
              <span className="absolute top-4 left-4 rounded-full bg-[#f5a524] px-3 py-1 text-xs font-bold text-[#0a1f33]">{c.zone}</span>
            </div>
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <p className="text-[#f5a524] font-heading tracking-widest uppercase text-xs">Featured catch</p>
              <h3 className="font-display text-4xl md:text-5xl text-white mt-2" data-testid="catch-species">{c.species}</h3>
              <p className="text-slate-300 mt-4 text-base leading-relaxed">{c.caption}</p>
              <div className="flex flex-wrap gap-3 mt-8">
                <Link to={c.trip}>
                  <Button className="btn-gold h-12 px-6 gap-2" data-testid="catch-book-trip">Book this trip <ArrowRight className="h-4 w-4" /></Button>
                </Link>
                <Button variant="outline" className="h-12 px-6 border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white gap-2" onClick={() => openDeposit()} data-testid="catch-whatsapp">
                  <MessageCircle className="h-4 w-4" /> Ask on WhatsApp
                </Button>
              </div>
              <div className="flex items-center gap-3 mt-8">
                <button onClick={() => go(-1)} className="grid place-items-center h-10 w-10 rounded-full border border-white/20 text-white transition hover:bg-white/10" aria-label="Previous catch" data-testid="catch-prev"><ChevronLeft className="h-5 w-5" /></button>
                <div className="flex gap-2">
                  {featuredCatches.map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setIdx(i)}
                      className={`h-2.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#f5a524]" : "w-2.5 bg-white/25 hover:bg-white/40"}`}
                      aria-label={`Show catch ${i + 1}`}
                      data-testid={`catch-dot-${i}`}
                    />
                  ))}
                </div>
                <button onClick={() => go(1)} className="grid place-items-center h-10 w-10 rounded-full border border-white/20 text-white transition hover:bg-white/10" aria-label="Next catch" data-testid="catch-next"><ChevronRight className="h-5 w-5" /></button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const { t } = useTranslation();
  const { openDeposit } = useDeposit();
  const trips = useTrips();
  const species = useSpeciesList();
  const testimonials = useTestimonials();
  return (
    <>
      <Seo
        title="Hog Tide Fishing | San Juan del Sur Nicaragua Sport Fishing Charters"
        description="Private offshore & inshore sport fishing charters, surf drops & sunset cruises in San Juan del Sur, Nicaragua. Marlin, sailfish, roosterfish, tuna & dorado. Book your Pacific adventure."
        path="/"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": ["LocalBusiness", "TravelAgency"],
          name: site.name,
          image: site.logo,
          "@id": "https://hogtidefishing.com",
          url: "https://hogtidefishing.com",
          telephone: site.phoneRaw,
          email: site.email,
          priceRange: "$$",
          description: "Private sport fishing charters in San Juan del Sur, Nicaragua — offshore & inshore fishing, surf drops and sunset cruises.",
          address: { "@type": "PostalAddress", addressLocality: "San Juan del Sur", addressRegion: "Rivas", addressCountry: "NI" },
          geo: { "@type": "GeoCoordinates", latitude: 11.2528, longitude: -85.8703 },
          areaServed: "San Juan del Sur, Nicaragua",
          sameAs: [site.social.facebook, site.social.instagram],
          aggregateRating: { "@type": "AggregateRating", ratingValue: "4.9", reviewCount: "4" },
        }}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden">
        <HeroMedia />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a1f33] via-[#0a1f33]/50 to-[#0a1f33]/30" />
        <div className="container-x relative z-10 pt-24 pb-16">
          <div className="animate-fadeUp">
            <Eyebrow>{t("home.heroEyebrow")}</Eyebrow>
            <h1 className="font-display text-5xl sm:text-6xl md:text-7xl text-white leading-[0.98] text-shadow-hero max-w-4xl">
              {t("home.heroTitle")}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-200 max-w-2xl">
              {t("home.heroSub")}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/plan-your-trip">
                <Button className="btn-gold h-14 px-8 text-base gap-2"><Compass className="h-5 w-5" /> {t("common.planYourTrip")}</Button>
              </Link>
              <Button variant="outline" className="h-14 px-8 text-base border-white/40 bg-white/5 text-white hover:bg-white/15 hover:text-white gap-2" onClick={() => openDeposit()}>
                <MessageCircle className="h-5 w-5" /> {t("common.bookWhatsApp")}
              </Button>
            </div>
            <p className="mt-4 flex items-center gap-2 text-sm text-slate-300" data-testid="hero-whatsapp-note">
              <span className="relative flex h-2.5 w-2.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-75" />
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#25D366]" />
              </span>
              {t("header.replyNote")}
            </p>
            <div className="flex flex-wrap gap-6 mt-10 text-sm text-slate-200">
              <span className="flex items-center gap-2"><Gauge className="h-5 w-5 text-[#f5a524]" /> {t("home.statEngines")}</span>
              <span className="flex items-center gap-2"><Users className="h-5 w-5 text-[#f5a524]" /> {t("home.statPrivate")}</span>
              <span className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-[#f5a524]" /> {t("home.statCrew")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ESTABLISHED DIVIDER */}
      <div className="bg-[#071726] border-y border-white/5">
        <div className="container-x flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 py-5 text-center">
          <span className="hidden sm:block h-px w-16 bg-gradient-to-r from-transparent to-[#f5a524]/60" />
          <p className="font-heading tracking-wide text-slate-200 text-sm md:text-base">
            {t("footer.established")}
          </p>
          <a href={site.social.facebook} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-[#1877F2] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#0f66d0]">
            <Facebook className="h-4 w-4" /> {t("footer.followUs")}
          </a>
          <span className="hidden sm:block h-px w-16 bg-gradient-to-l from-transparent to-[#f5a524]/60" />
        </div>
      </div>

      {/* INTRO */}
      <section className="py-20">
        <div className="container-x grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <Eyebrow>{t("home.introEyebrow")}</Eyebrow>
            <h2 className="font-display text-4xl md:text-5xl text-white leading-tight">{t("home.introTitle")}</h2>
            <p className="text-slate-300 mt-4">{t("home.introP1")}</p>
            <p className="text-slate-300 mt-3">{t("home.introP2")}</p>
            <div className="grid grid-cols-3 gap-4 mt-8">
              {[["500+", t("home.statHp")], ["12", t("home.statGuests")], ["4.9★", t("home.statRating")]].map(([n, l]) => (
                <div key={l} className="rounded-xl bg-[#0e2740] border border-white/10 p-4 text-center">
                  <p className="font-display text-3xl text-[#f5a524]">{n}</p>
                  <p className="text-xs text-slate-400 mt-1">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <Reveal delay={0.15} className="grid grid-cols-2 gap-4">
            <img src={heroImages[4].img} alt="Fishing action" className="rounded-2xl h-64 w-full object-cover mt-8" />
            <img src={site.logo ? gallery[0].src : ""} alt="On the water" className="rounded-2xl h-64 w-full object-cover" />
          </Reveal>
        </div>
      </section>

      {/* PLAN YOUR TRIP BANNER */}
      <section className="py-4">
        <div className="container-x">
          <Reveal>
            <div className="relative overflow-hidden rounded-3xl border border-[#f5a524]/30 bg-gradient-to-r from-[#0e2740] to-[#123a5c] p-8 md:p-12">
              <div className="relative z-10 md:flex items-center justify-between gap-8">
                <div>
                  <Eyebrow>{t("home.planEyebrow")}</Eyebrow>
                  <h3 className="font-display text-3xl md:text-4xl text-white leading-tight max-w-xl">{t("home.planTitle")}</h3>
                  <p className="text-slate-300 mt-3 max-w-lg">{t("home.planSub")}</p>
                </div>
                <Link to="/plan-your-trip" className="shrink-0 mt-6 md:mt-0">
                  <Button className="btn-gold h-14 px-8 text-base gap-2">{t("home.planBtn")} <ArrowRight className="h-5 w-5" /></Button>
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* TOURS */}
      <section className="py-20">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <SectionTitle eyebrow={t("home.tripsEyebrow")} title={t("home.tripsTitle")} sub={t("home.tripsSub")} />
            <Link to="/trips" className="mb-10"><Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white">View all trips</Button></Link>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {trips.slice(0, 3).map((t, i) => (
              <Reveal key={t.slug} delay={i * 0.1}><TripCard trip={t} /></Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="py-16 bg-[#071726]">
        <div className="container-x grid gap-8 md:grid-cols-4">
          {[
            { icon: Fish, t: "World-class fishing", d: "Billfish, tuna, roosterfish & more — inshore and offshore." },
            { icon: Ship, t: "Premium boat", d: "Fast twin Yamaha-powered vessel built for comfort and safety." },
            { icon: Waves, t: "Surf drops", d: "Direct boat access to three famous, uncrowded surf breaks." },
            { icon: Compass, t: "Private & custom", d: "Just your crew. Trips tailored around what you want." },
          ].map((f, i) => (
            <Reveal key={f.t} delay={i * 0.08}>
              <div className="text-center md:text-left">
                <div className="inline-flex rounded-xl bg-[#f5a524]/15 p-3 text-[#f5a524]"><f.icon className="h-7 w-7" /></div>
                <h3 className="font-heading text-xl text-white mt-3">{f.t}</h3>
                <p className="text-sm text-slate-400 mt-1">{f.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CATCH OF THE WEEK */}
      <CatchOfTheWeek />

      {/* SPECIES */}
      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow={t("home.speciesEyebrow")} title={t("home.speciesTitle")} sub={t("home.speciesSub")} />
          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {species.slice(0, 4).map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.08}><SpeciesCard s={s} /></Reveal>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/species"><Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white gap-2">Explore all species <ArrowRight className="h-4 w-4" /></Button></Link>
          </div>
        </div>
      </section>

      {/* GALLERY STRIP */}
      <section className="py-6">
        <div className="container-x">
          <SectionTitle eyebrow={t("home.galleryEyebrow")} title={t("home.galleryTitle")} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {gallery.slice(0, 8).map((g, i) => (
              <Reveal key={i} delay={(i % 4) * 0.06}>
                <Link to="/gallery" className="block group overflow-hidden rounded-xl">
                  <img src={g.src} alt="Hog Tide gallery" className="h-40 w-full object-cover transition duration-500 group-hover:scale-110" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20">
        <div className="container-x">
          <SectionTitle center eyebrow={t("home.testimonialsEyebrow")} title={t("home.testimonialsTitle")} />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((r, i) => (
              <Reveal key={r.name} delay={i * 0.08}>
                <div className="h-full rounded-2xl bg-[#0e2740] border border-white/10 p-6">
                  <StarRow n={r.rating} />
                  <p className="text-slate-300 text-sm mt-3 leading-relaxed">"{r.text}"</p>
                  <p className="font-heading text-white mt-4">{r.name}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MARINE WEATHER */}
      <section className="py-16">
        <div className="container-x">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <SectionTitle eyebrow="Plan the perfect day" title="Live marine forecast" sub="Real-time swell, wind and temperature for San Juan del Sur. We flag the best day to fish." />
            <Link to="/weather" className="mb-10"><Button variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10 hover:text-white gap-2"><CloudSun className="h-4 w-4" /> Full forecast</Button></Link>
          </div>
          <Reveal><WeatherWidget compact /></Reveal>
          <div className="mt-6"><Reveal><TideTracker compact /></Reveal></div>
        </div>
      </section>

      <Partners />

      <CTASection image={heroImages[2].img} />
    </>
  );
};

export default Home;
